import { useActiveContextScope } from "@/features/active-context";
import {
  ActivityFeed,
  ConnectChat,
  ConnectMessageThread,
  GrantManager,
  MemberManager,
  SpaceScopeBanner,
  useConnectSpace,
  useOpenAreaInChat,
} from "@/features/connect";
import { validateWorkspaceSearch } from "@/features/workspace-router";
import {
  type ConnectAskResponse,
  type ConnectGrant,
  type ConnectSpaceDetail,
  askInSpace,
} from "@ando/core";
import {
  Badge,
  Button,
  Skeleton,
  SurfaceHeader,
  SurfaceLayout,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  Activity,
  ArrowLeft,
  Blend,
  Bot,
  Building2,
  Eye,
  FileText,
  MessageSquareText,
  MessagesSquare,
  Network,
  Users2,
} from "lucide-react";
import * as React from "react";

/**
 * Ando Connect — Space detail SHELL (owner console).
 *
 * Layout: a back link + header (name + mode badge), then a two-column body:
 *   - LEFT (hero)  → the persistent ``<SpaceScopeBanner>`` (trust centerpiece)
 *     above the ``<ConnectChat>`` wired to ``askInSpace(spaceId, …)`` so the
 *     owner can preview the guest experience against the live grant scope.
 *   - RIGHT (rail) → ``Tabs``: Risorse condivise / Membri & accesso /
 *     Attività. Each tab mounts its management form — ``GrantManager`` (add /
 *     revoke grants), ``MemberManager`` (invite / revoke members, chatbot
 *     config), ``ActivityFeed`` (owner access log). The mutation hooks they
 *     share live in ``useConnectMutations`` so no contract is re-derived here.
 *
 * No fake data: counts/lists come straight from ``getSpace`` detail; empty
 * sections render an honest empty-state, never placeholder rows.
 */
export const Route = createFileRoute("/_authed/workspace/connect/$spaceId")({
  validateSearch: validateWorkspaceSearch,
  component: ConnectSpaceDetailRoute,
});

type RailTab = "resources" | "members" | "discussion" | "activity";

function ConnectSpaceDetailRoute() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const { spaceId } = useParams({
    from: "/_authed/workspace/connect/$spaceId",
  });
  const { data: space, isLoading, isError } = useConnectSpace(spaceId);
  const [tab, setTab] = React.useState<RailTab>("resources");

  // The chat transport is bound to THIS space's authed /ask endpoint. The
  // component stays transport-agnostic; we inject the binding here.
  const ask = React.useCallback(
    (question: string): Promise<ConnectAskResponse> => askInSpace(spaceId, { question }),
    [spaceId],
  );
  // June 14, 2026 — open a real chat scoped to this area (the owner can query
  // their own area with the full chat UX, not just the inline preview).
  const openAreaInChat = useOpenAreaInChat();

  const grants = space?.grants ?? [];
  const members = space?.members ?? [];
  const mode = space?.mode === "chatbot" ? "chatbot" : "collaboration";
  const isChatbot = mode === "chatbot";
  const isArchived = space?.status === "archived";

  // Active context — the agent grounds "share this space with X",
  // "summarise what's in it", "who has access" against THIS space.
  useActiveContextScope(
    space
      ? {
          kind: "connect_space",
          id: space.id,
          title: space.name,
          summary: [mode, space.status].filter(Boolean).join(" · ") || undefined,
          meta: {
            mode,
            ...(space.status ? { status: space.status } : {}),
            member_count: members.length,
            grant_count: grants.length,
          },
        }
      : null,
  );

  // Mode-aware rail. A CHATBOT space is public + anonymous: it has no per-guest
  // members and no member-to-member thread, so its rail is Risorse ·
  // Pubblicazione (the ChatbotPanel — public link / embed / caps) · Attività.
  // A COLLABORATION space keeps the full Risorse · Membri · Discussione ·
  // Attività console. The "members" slot is reused for both (MemberManager
  // internally swaps to the ChatbotPanel when mode==='chatbot' — DRY), only its
  // label/icon change. ``activeTab`` clamps to a tab that exists for the mode.
  const tabValues: readonly RailTab[] = isChatbot
    ? ["resources", "members", "activity"]
    : ["resources", "members", "discussion", "activity"];
  const activeTab = tabValues.includes(tab) ? tab : "resources";
  // Viewer relationship to THIS Space (cross-org phase). The BE sets
  // ``is_owner`` / ``viewer_role``; absent ⇒ treat as owner so pre-migration
  // detail loads keep the full console. A non-owner is a cross-org member who
  // must see the read-only member view, NOT the management surfaces.
  const isOwner = space ? space.is_owner !== false && space.viewer_role !== "member" : true;

  // Canonical SurfaceHeader ``label`` slot — the back link to the hub, styled
  // like every other workspace detail route (cf. ``flows.$flowId``).
  const backLabel = (
    <Link
      to="/workspace/connect"
      className="inline-flex items-center gap-1 text-ink-3 transition-colors hover:text-ink-1 focus-visible:text-ink-1 focus-visible:outline-none"
    >
      <ArrowLeft size={12} strokeWidth={2} aria-hidden />
      <Trans>Spazi Connect</Trans>
    </Link>
  );

  if (isError) {
    return (
      <div className="flex h-full flex-col">
        <div className="px-8 pt-7">{backLabel}</div>
        <div className="m-auto flex max-w-sm flex-col items-center gap-3 text-center">
          <span
            aria-hidden
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-cream-deep text-ink-3"
          >
            <Network size={18} strokeWidth={1.75} />
          </span>
          <p className="font-semibold text-[14px] text-ink-1">
            <Trans>Spazio non disponibile</Trans>
          </p>
          <p className="text-[12.5px] text-ink-2">
            <Trans>
              Non riusciamo a caricare questo spazio. Potrebbe essere stato archiviato o l'accesso è
              stato revocato.
            </Trans>
          </p>
          <button
            type="button"
            onClick={() => void navigate({ to: "/workspace/connect" })}
            className={cn(
              "hover:-translate-y-px rounded-md bg-ink-2 px-3 py-1.5 text-[12px] text-white transition-transform",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/55 focus-visible:ring-offset-2 focus-visible:ring-offset-cream",
            )}
          >
            <Trans>Torna agli spazi</Trans>
          </button>
        </div>
      </div>
    );
  }

  // ── MEMBER view (cross-org) ───────────────────────────────────────────
  // A real Ando user invited from another org opens a Space they DON'T own.
  // June 14, 2026 — consumption moved INTO the main chat: instead of a
  // separate read-only mini-chat, the member view leads with "Interroga in
  // chat" (opens a real conversation scoped to this area) + the read-only
  // scope banner + the member-to-member Discussione. Every owner-only control
  // stays hidden (defense-in-depth on top of the BE 403s).
  if (!isLoading && space && !isOwner) {
    return (
      <MemberSpaceView
        space={space}
        grants={grants}
        isArchived={isArchived}
        backLabel={backLabel}
      />
    );
  }

  return (
    <SurfaceLayout
      header={
        <SurfaceHeader
          label={backLabel}
          title={
            isLoading ? (
              <Skeleton shape="text" className="h-7 w-64" />
            ) : (
              <span className="block truncate">{space?.name}</span>
            )
          }
          meta={
            space?.description ? (
              <span className="block max-w-2xl truncate">{space.description}</span>
            ) : undefined
          }
          actions={
            !isLoading && space ? (
              <>
                {/* Query this area with the FULL chat UX (not just the inline
                    preview). Collaboration only — a public chatbot IS its own
                    consumption surface. */}
                {!isChatbot && !isArchived ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="gap-1.5"
                    onClick={() =>
                      openAreaInChat({
                        id: space.id,
                        name: space.name,
                        ownerOrgName:
                          typeof space.owner_org_name === "string" ? space.owner_org_name : null,
                      })
                    }
                  >
                    <MessageSquareText size={13} strokeWidth={2} aria-hidden />
                    <Trans>Apri in chat</Trans>
                  </Button>
                ) : null}
                <Badge
                  variant={mode === "chatbot" ? "accent" : "secondary"}
                  className="gap-1 py-0.5 text-[10.5px]"
                >
                  {mode === "chatbot" ? (
                    <Bot size={11} strokeWidth={2.2} aria-hidden />
                  ) : (
                    <Users2 size={11} strokeWidth={2.2} aria-hidden />
                  )}
                  {mode === "chatbot" ? <Trans>Chatbot</Trans> : <Trans>Collaborazione</Trans>}
                </Badge>
                {isArchived ? (
                  <Badge variant="warning" className="py-0.5 text-[10.5px]">
                    <Trans>Archiviato</Trans>
                  </Badge>
                ) : null}
              </>
            ) : undefined
          }
        />
      }
      contentClassName="overflow-hidden px-0 pb-0"
    >
      {/* Body — chat hero (left) + management rail (right). */}
      <div className="grid h-full min-h-0 grid-cols-1 gap-5 px-8 py-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        {/* LEFT — owner preview of the guest chat, scoped by live grants. */}
        <div className="flex min-h-0 flex-col">
          <ConnectChat
            ask={ask}
            scopeBanner={
              isLoading ? (
                <Skeleton shape="rect" className="h-16 w-full rounded-[14px]" />
              ) : (
                <SpaceScopeBanner grants={grants} readOnly />
              )
            }
            disabled={isLoading || isArchived}
            title={isChatbot ? i18n._(msg`Anteprima chatbot`) : i18n._(msg`Anteprima ospite`)}
            subtitle={
              isChatbot
                ? i18n._(
                    msg`Provi le stesse risposte che riceverà chi usa il tuo chatbot pubblico, limitate alle fonti condivise.`,
                  )
                : i18n._(
                    msg`Provi le stesse risposte che vedono i tuoi ospiti, limitate alle fonti condivise.`,
                  )
            }
            emptyHint={
              isArchived
                ? i18n._(msg`Questo spazio è archiviato: gli ospiti non possono più interrogarlo.`)
                : undefined
            }
            className="flex-1"
          />
        </div>

        {/* RIGHT — management rail. */}
        <aside className="flex min-h-0 flex-col">
          <Tabs
            value={activeTab}
            onValueChange={(v) => setTab(v as RailTab)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <TabsList className={cn("grid w-full", isChatbot ? "grid-cols-3" : "grid-cols-4")}>
              <TabsTrigger value="resources" className="gap-1.5 text-[12px]">
                <FileText size={13} strokeWidth={2} aria-hidden />
                <Trans>Risorse</Trans>
              </TabsTrigger>
              {/* The "members" slot doubles as the chatbot PUBLISH surface — same
                  MemberManager mount, mode-aware label/icon (Bot for chatbot). */}
              <TabsTrigger value="members" className="gap-1.5 text-[12px]">
                {isChatbot ? (
                  <Bot size={13} strokeWidth={2} aria-hidden />
                ) : (
                  <Users2 size={13} strokeWidth={2} aria-hidden />
                )}
                {isChatbot ? <Trans>Pubblicazione</Trans> : <Trans>Membri</Trans>}
              </TabsTrigger>
              {/* Member-to-member discussion is collaboration-only: a public
                  chatbot has no members to chat with. */}
              {isChatbot ? null : (
                <TabsTrigger value="discussion" className="gap-1.5 text-[12px]">
                  <MessagesSquare size={13} strokeWidth={2} aria-hidden />
                  <Trans>Discussione</Trans>
                </TabsTrigger>
              )}
              <TabsTrigger value="activity" className="gap-1.5 text-[12px]">
                <Activity size={13} strokeWidth={2} aria-hidden />
                <Trans>Attività</Trans>
              </TabsTrigger>
            </TabsList>

            {/* ── Risorse condivise ──────────────────────────────────── */}
            <TabsContent value="resources" className="min-h-0 flex-1 overflow-y-auto">
              {isLoading ? (
                <RailSkeleton />
              ) : space ? (
                <GrantManager spaceId={space.id} spaceName={space.name} grants={grants} />
              ) : null}
            </TabsContent>

            {/* ── Membri & accesso ───────────────────────────────────── */}
            <TabsContent value="members" className="min-h-0 flex-1 overflow-y-auto">
              {isLoading ? (
                <RailSkeleton />
              ) : space ? (
                <MemberManager space={space} members={members} />
              ) : null}
            </TabsContent>

            {/* ── Discussione (member-to-member human thread) — collaboration
                only; a public chatbot has no member thread. ──────────── */}
            {isChatbot ? null : (
              <TabsContent value="discussion" className="min-h-0 flex-1">
                {space ? <ConnectMessageThread spaceId={space.id} className="h-full" /> : null}
              </TabsContent>
            )}

            {/* ── Attività ───────────────────────────────────────────── */}
            <TabsContent value="activity" className="min-h-0 flex-1 overflow-y-auto">
              {/* Owner access log. An empty or failed load shows an honest
                  empty-state — never fabricated rows. Only fetch when active. */}
              {space ? <ActivityFeed spaceId={space.id} enabled={tab === "activity"} /> : null}
            </TabsContent>
          </Tabs>
        </aside>
      </div>
    </SurfaceLayout>
  );
}

/**
 * MemberSpaceView — the cross-org MEMBER surface for a Space owned by ANOTHER
 * org. June 14, 2026: consumption moved into the MAIN chat. Instead of a second
 * read-only mini-chat, the member view leads with an "Apri in chat" hero (opens
 * a real conversation scoped to this area — same chat UX, answers ONLY from the
 * shared slice) above the read-only ``<SpaceScopeBanner>`` (what's queryable),
 * with the member-to-member Discussione + participants alongside. ZERO owner
 * controls (no GrantManager / MemberManager / ChatbotPanel / ActivityFeed).
 */
function MemberSpaceView({
  space,
  grants,
  isArchived,
  backLabel,
}: {
  readonly space: ConnectSpaceDetail;
  readonly grants: readonly ConnectGrant[];
  readonly isArchived: boolean;
  readonly backLabel: React.ReactNode;
}) {
  const { i18n } = useLingui();
  const openAreaInChat = useOpenAreaInChat();
  const ownerOrg =
    typeof space.owner_org_name === "string" && space.owner_org_name.trim()
      ? space.owner_org_name.trim()
      : null;
  const members = space.members ?? [];

  return (
    <SurfaceLayout
      header={
        <SurfaceHeader
          label={backLabel}
          title={<span className="block truncate">{space.name}</span>}
          // Counterpart is the OWNING org ("Spazio di {org}").
          meta={
            <span className="inline-flex items-center gap-1.5">
              <Building2 size={13} strokeWidth={2} aria-hidden />
              {ownerOrg ? (
                <Trans>Spazio di {ownerOrg}</Trans>
              ) : (
                <Trans>Spazio condiviso con te</Trans>
              )}
            </span>
          }
          actions={
            <>
              <Badge
                variant="secondary"
                className="gap-1 py-0.5 text-[10.5px]"
                aria-label={i18n._(msg`Accesso in sola lettura`)}
              >
                <Eye size={11} strokeWidth={2.2} aria-hidden />
                <Trans>Sola lettura</Trans>
              </Badge>
              {isArchived ? (
                <Badge variant="warning" className="py-0.5 text-[10.5px]">
                  <Trans>Archiviato</Trans>
                </Badge>
              ) : null}
            </>
          }
        />
      }
      contentClassName="overflow-hidden px-0 pb-0"
    >
      {/* Body — "interroga in chat" hero + scope (left) + the Discussione
          thread & participants (right). No owner tabs, no authoring controls. */}
      <div className="grid h-full min-h-0 grid-cols-1 gap-5 px-8 py-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-h-0 flex-col gap-4">
          {/* Primary action: query this area in the real chat. */}
          <div className="flex flex-col gap-3 rounded-[18px] border border-hairline/60 bg-cream px-5 py-5">
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="mt-px flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-hairline text-ando-orange"
              >
                <Blend size={17} strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <h2 className="font-[550] font-display text-[15px] text-ink-1 leading-snug">
                  <Trans>Interroga quest'area in chat</Trans>
                </h2>
                <p className="mt-0.5 text-[12.5px] text-ink-2 leading-relaxed">
                  {ownerOrg ? (
                    <Trans>
                      Apri una chat Ando — stessa esperienza di sempre — che risponde solo dalle
                      fonti che {ownerOrg} ha condiviso qui, in sola lettura.
                    </Trans>
                  ) : (
                    <Trans>
                      Apri una chat Ando — stessa esperienza di sempre — che risponde solo dalle
                      fonti condivise in quest'area, in sola lettura.
                    </Trans>
                  )}
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              size="md"
              className="w-full justify-center gap-1.5"
              disabled={isArchived}
              onClick={() =>
                openAreaInChat({
                  id: space.id,
                  name: space.name,
                  ownerOrgName: ownerOrg,
                })
              }
            >
              <MessageSquareText size={14} strokeWidth={2.2} aria-hidden />
              {isArchived ? <Trans>Area archiviata</Trans> : <Trans>Apri in chat</Trans>}
            </Button>
          </div>

          {/* What you can query — the read-only scope, never by colour alone. */}
          <SpaceScopeBanner
            grants={grants}
            readOnly
            heading={<Trans>Interroghi solo le fonti condivise con te</Trans>}
          />
        </div>

        {/* RIGHT — member-to-member discussion (the SAME human thread as the
            owner tab; no guest credential — this is a JWT member) above an
            optional read-only participants roster (informational only). */}
        <aside className="flex min-h-0 flex-col gap-4">
          <section className="flex min-h-0 flex-1 flex-col">
            <h2 className="flex items-center gap-1.5 px-0.5 pb-2 font-semibold text-[12px] text-ink-2 uppercase tracking-[0.04em]">
              <MessagesSquare size={13} strokeWidth={2} aria-hidden />
              <Trans>Discussione</Trans>
            </h2>
            <ConnectMessageThread spaceId={space.id} className="min-h-0 flex-1" />
          </section>

          {members.length > 0 ? (
            <section className="shrink-0">
              <h2 className="flex items-center gap-1.5 px-0.5 pb-2 font-semibold text-[12px] text-ink-2 uppercase tracking-[0.04em]">
                <Users2 size={13} strokeWidth={2} aria-hidden />
                <Trans>Partecipanti</Trans>
              </h2>
              <ul className="flex flex-col divide-y divide-hairline/60 rounded-lg border border-hairline/60">
                {members.map((m) => {
                  const label = (m.email ?? "").trim() || i18n._(msg`Partecipante`);
                  const initial = label.charAt(0).toUpperCase() || "?";
                  return (
                    <li
                      key={m.id}
                      className="flex items-center gap-2.5 px-3 py-2.5"
                    >
                      <span
                        aria-hidden
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cream-deep font-semibold text-[12px] text-ink-1"
                      >
                        {initial}
                      </span>
                      <p className="min-w-0 flex-1 truncate font-medium text-[12.5px] text-ink-1">
                        {label}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
        </aside>
      </div>
    </SurfaceLayout>
  );
}
MemberSpaceView.displayName = "MemberSpaceView";

function RailSkeleton() {
  return (
    <div className="flex flex-col gap-2 py-2">
      {["r1", "r2", "r3"].map((k) => (
        <Skeleton key={k} shape="rect" className="h-11 w-full rounded-lg" />
      ))}
    </div>
  );
}
